import React, { useState, useEffect, useReducer } from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import css from "./ReserveTimeModal.css";
import Footer from "../../common/Footer";
import StyledButton from "../../common/StyledButton";
import Header from '../../common/Header';
import Form from "./Form";
import { isFormValid } from "../../utils/reserveTime";
import useStarfishAPI from "hooks/useStarfishAPI";
import { urlParams } from "utils/request";
import moment from "moment";
import { isEmpty } from "lodash";
import { reducer, getInitialState } from './reducer';
import Toast from 'common/Toast';
import EditInfo from './EditInfo';

const ReserveTimeModal = props => {
  Modal.setAppElement(document.getElementById("main-center"));  
  
  const [{
    description,
    startDate,
    endDate,
    startTime,
    endTime,
    repeat,
    categories,
    category,
    calendar
  }, dispatch] = useReducer(reducer, getInitialState({
    startDate: props.startDate,
    startTime: props.startTime,
    duration: props.duration,
    description: props.description,
    calendarOptions: props.calendarOptions
  }));
  const [shouldValidate, setShouldValidate] = useState(false);
  const [response, callAPI] = useStarfishAPI();
  const [toastMessage, setToastMessage] = useState("");

  const isEdit = !!props.appointmentId;

  // Validate all fields that are subject to UI errors
  const canSubmit = isFormValid(
    description, 
    startDate, 
    repeat, 
    endDate,
    categories,
    category,
    props.calendarOptions, 
    calendar,
    startTime,
    endTime,
    isEdit
  );

  const onSubmit = () => {
    if ( response.pending || !canSubmit ) {
      // If the user attempted to submit for the first time and some of the fields are
      // invalid, then we display the error messages
      if ( !shouldValidate ) setShouldValidate(true);
      return;
    }

    callAPI(`../appointments/saveAppointment.html`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      body: urlParams({
        scheduleId: isEmpty(calendar) ? props.partyScheduleId : calendar.value,
        startDate: moment(startDate).format("YYYY-MM-DD"),
        description,
        meetingType: "ReservedTime",
        startTime: startTime.value,
        endTime: endTime.value,
        duration: endTime.date.diff(startTime.date, 'minutes'),
        ...(!isEmpty(category) && { reservedTimeCategoryId: category.value }),
        ...(repeat.value === true && {
          recurring: true,
          recurEndDate: moment(endDate).format("YYYY-MM-DD")
        }),
        ...(props.appointmentId && { appId: props.appointmentId })
      }, false)
    });
  };

  const isButtonDisabled = response.pending || (shouldValidate && !canSubmit);

  const getPrimaryButtonText = () => {
    if ( response.pending ) return "Submitting...";
    if ( isEdit ) return "Submit";
    return "Add to Calendar";
  };

  useEffect(() => {
    if ( response.success ) props.closeModal();
  }, [response]);

  useEffect(() => {
    if ( response.error ) setToastMessage(response.error);
  }, [response]);

  return (
    <>
      <Modal
        isOpen={props.isOpen}
        onRequestClose={props.closeModal}
        overlayClassName={css["overlay"]}
        className={css["modal"]}
        shouldCloseOnOverlayClick={false}
        contentLabel="Reserve Time Modal"
      >
        <div className={css["container"]}>
          <Header closeModal={props.closeModal} />
          {isEdit && <EditInfo 
            message="You are editing a single reserve time occurrence. You cannot edit a reserve time series." 
          />}
          <div className={css["content"]}>
            <h1>Reserve Time</h1>
            <h2>
              Complete the items below to mark time on your calendar as
              unavailable for appointments.
            </h2>
            <p>
              <span aria-hidden>*</span> All Fields Required
            </p>
            <Form
              setValue={(field, value) => dispatch({ type: "SET_VALUE", field, value })}
              calendarOptions={props.calendarOptions}
              calendar={calendar}
              categories={categories}
              category={category}
              categoryId={props.categoryId}
              description={description}
              startDate={startDate}
              endDate={endDate}
              startTime={startTime}
              endTime={endTime}
              repeat={repeat}
              shouldValidate={shouldValidate}
              isEdit={isEdit}
            />
          </div>
          <Footer
            primaryButton={
              <StyledButton aria-disabled={isButtonDisabled} onClick={onSubmit}>
                {getPrimaryButtonText()}
              </StyledButton>
            }
            secondaryButton={
              <StyledButton asLink onClick={props.closeModal}>
                Cancel
              </StyledButton>
            }
          />
        </div>
      </Modal>
      {toastMessage && <Toast 
        message={toastMessage} 
        onTimeout={() => setToastMessage("")} 
      />}
    </>
  );
};

ReserveTimeModal.propTypes = {
  calendarOptions: PropTypes.array.isRequired,
  isOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  partyScheduleId: PropTypes.number.isRequired,
  appointmentId: PropTypes.string,
  categoryId: PropTypes.number,
  description: PropTypes.string,
  startDate: PropTypes.string,
  startTime: PropTypes.string,
  duration: PropTypes.number,
};

export default ReserveTimeModal;
